import React, { useEffect, useState } from "react";
import RequireAuth from "routes/RequireAuth";
import AppLayout from "layouts/AppLayout";
import { Button, Grid, Skeleton, TextField, Typography } from "@mui/material";
import WeatherScenarioAPI, {
  ConditionGroupCombinators,
  ConditionOperators,
  GeotargetingTypes,
  IConditionGroup,
  IWeatherScenario,
  IGeotarget,
} from "api/scenarios";
import { useCurrentProject } from "hooks/projects";
import { useParams } from "react-router-dom";
import { isEmpty } from "lodash";
import WeatherQueryBuilder from "components/WeatherQueryBuilder/WeatherQueryBuilder";
import { FIELD_NAMES } from "components/WeatherQueryBuilder/constants";
import { Save } from "@mui/icons-material";
import { enqueueSnackbar } from "notistack";
import { axios500Error, successMessage } from "../../content/weather";

const initialScenario: IWeatherScenario = {
  active: true,
  deleted: false,
  rule: {
    group: {
      combinator: ConditionGroupCombinators.AND,
      rules: [
        {
          field: FIELD_NAMES.status,
          operator: ConditionOperators.IN,
          value: ["800"],
        },
      ],
    },
    geotargeting: {
      type: GeotargetingTypes.COUNTRY,
      identifiers: ["PL"],
    },
  },
};

const WeatherEdit = () => {
  const [scenario, setScenario] = useState<IWeatherScenario>(initialScenario);
  const [currentProject] = useCurrentProject();
  const [loading, setLoading] = useState(false);
  const { scenarioId } = useParams();

  useEffect(() => {
    const fetchScenario = async () => {
      const response = await WeatherScenarioAPI.getScenario(
        scenarioId as unknown as number,
        currentProject.id
      );
      setScenario(response);
    };

    if (!isEmpty(currentProject)) {
      setLoading(true);
      try {
        fetchScenario();
      } finally {
        setLoading(false);
      }
    }
  }, [currentProject.id, scenarioId]);

  const handleSaveScenario = async (scenario: IWeatherScenario) => {
    setLoading(true);
    try {
      const response = await WeatherScenarioAPI.updateScenario(
        scenarioId as unknown as number,
        currentProject.id,
        scenario
      );
      setScenario(response);
      enqueueSnackbar(successMessage, { variant: "success" });
    } catch (e) {
      enqueueSnackbar(axios500Error, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const setScenarioName = (name: string) => {
    setScenario((prevState) => ({
      ...prevState,
      name: name,
    }));
  };

  const setWeatherRule = (newRule: IConditionGroup) => {
    setScenario((prevState) => ({
      ...prevState,
      rule: { geotargeting: prevState.rule.geotargeting, group: newRule },
    }));
  };

  const setGeotarget = (newGeotarget: IGeotarget) => {
    setScenario((prevState) => ({
      ...prevState,
      rule: {
        geotargeting: newGeotarget,
        group: prevState.rule.group,
      },
    }));
  };

  const loadingScreen = (
    <Grid item xs={12}>
      <Skeleton variant="text" />
      <Skeleton variant="text" />
      <Skeleton variant="rectangular" height={440} />
    </Grid>
  );

  return (
    <RequireAuth>
      <AppLayout>
        <Grid container rowSpacing={2}>
          {loading && loadingScreen}

          {!loading && (
            <React.Fragment>
              <Grid item xs={12}>
                <Typography variant="h5">Scenario name</Typography>
                <TextField
                  variant="standard"
                  value={scenario.name}
                  onChange={(e) => setScenarioName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <WeatherQueryBuilder
                  weatherQuery={scenario.rule.group}
                  onWeatherQueryChange={(query) => setWeatherRule(query)}
                  geotarget={scenario.rule.geotargeting}
                  onGeotargetChange={(target) => setGeotarget(target)}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={async () => await handleSaveScenario(scenario)}
                >
                  Save
                </Button>
              </Grid>
            </React.Fragment>
          )}
        </Grid>
      </AppLayout>
    </RequireAuth>
  );
};

export default WeatherEdit;
